/**
 * checkInOutAdapters — legacy form-state → Symfony DTO converters for the
 * in-modal Check Out / Check In flows in Calendar Month V2.
 *
 * Why these exist:
 *   1. Legacy AngularJS forms use `comments` (plural) for the check-in note.
 *      The Symfony `CheckInFinishRequestDto::$comment` is SINGULAR
 *      (`CalendarCheckInManager.php:141-143` calls `$dto->getComment()`).
 *      Forwarding the legacy field name would silently drop the note.
 *
 *   2. Legacy custom-field state is a map keyed by `customFieldType.id`
 *      (`{[id]: value}`). Symfony `CheckInFinishRequestDto::$customFields`
 *      expects an ARRAY of `{typeId, value}` objects, and rejects required
 *      fields whose `value` is empty (backend treats `null` and `''` as
 *      "missing"). Sending the raw map → 400.
 *
 *   3. Counters: legacy state may already reflect the user's edits in the
 *      `value` field. But `CalendarCheckInManager.php` uses
 *      `$counter['value'] ?? $current` to handle out-of-sequence math —
 *      i.e. it needs the OLD counter value preserved so it can compare
 *      against the new one. Sending only the new value confuses the
 *      backend math. The adapter ships both fields: `value` = old reading,
 *      `newValue` = the user's new reading.
 *
 *   4. Date strategy: numeric epoch seconds. `DateConvertManager::timestampConvert()`
 *      at `:134-159` accepts numeric input without going through the
 *      string-parsing path used for event dates. No tz-aware shenanigans
 *      needed (unlike maintenance's `formatMaintenanceDate` workaround).
 *
 * @module checkInOutAdapters
 */

/**
 * Shape a customFields map → DTO `[{typeId, value}]` array, dropping
 * entries whose value is empty (`null` / `undefined` / `''`). Empty
 * required fields would otherwise trigger backend validation errors.
 *
 * @param {Object<string|number, *>} customValues  Keyed by customField id.
 * @returns {Array<{typeId: number, value: *}>}
 */
export function shapeCustomFields(customValues) {
	if (!customValues || typeof customValues !== 'object') return [];
	return Object.entries(customValues)
		.map(([typeId, value]) => ({ typeId: Number(typeId), value }))
		.filter(entry => {
			if (entry.value === null || entry.value === undefined) return false;
			if (typeof entry.value === 'string' && entry.value.trim() === '') return false;
			return Number.isFinite(entry.typeId);
		});
}

/**
 * Shape a counters map → DTO `counters[]` preserving the OLD reading in
 * `value` and the new reading in `newValue`. Backend uses the OLD value
 * for out-of-sequence detection (`CalendarCheckInManager.php:299-331`):
 *   - If `newValue < value` → out-of-sequence warning surfaces in the UI.
 *   - If `newValue >= value` → counter advances normally.
 *
 * Caller is responsible for tracking the original counter reading (usually
 * the value returned from `check-in-start`) and the user's edit. We ship
 * both so backend can math.
 *
 * @param {Array<{id: number|string, value: number|string, newValue: number|string}>} counters
 * @returns {Array<{id: number, value: *, newValue: *}>}
 */
export function shapeCounters(counters) {
	if (!Array.isArray(counters)) return [];
	return counters
		.map(c => ({
			id: Number(c?.id),
			value: c?.value,
			newValue: c?.newValue
		}))
		.filter(c => Number.isFinite(c.id));
}

/**
 * Shape resources entries for check-in (per-resource new counter readings).
 * Symfony DTO field: `resources[]` with `{id, newCounterValue}`.
 *
 * @param {Array<{id: number|string, newCounterValue: number|string}>} resources
 * @returns {Array<{id: number, newCounterValue: *}>}
 */
export function shapeResources(resources) {
	if (!Array.isArray(resources)) return [];
	return resources
		.map(r => ({
			id: Number(r?.id),
			newCounterValue: r?.newCounterValue
		}))
		.filter(r => Number.isFinite(r.id));
}

/**
 * Adapt a legacy check-in form state to the `CheckInFinishRequestDto` shape.
 * Renames `comments` → `comment` (SINGULAR — see module docblock).
 *
 * @param {Object} form
 * @param {number|string} [form.actualArrivalTime]  Epoch seconds.
 * @param {string} [form.comments]                  Legacy plural; renamed.
 * @param {string} [form.comment]                   Pre-renamed; preferred.
 * @param {Object} [form.customValues]              `{[typeId]: value}` map.
 * @param {Array} [form.counters]                   `[{id, value, newValue}]`.
 * @param {Array} [form.resources]                  `[{id, newCounterValue}]`.
 * @returns {Object} `CheckInFinishRequestDto` shape.
 */
export function toCheckInFinishPayload(form) {
	const f = form || {};
	const payload = {
		comment: f.comment ?? f.comments ?? '',
		customFields: shapeCustomFields(f.customValues),
		counters: shapeCounters(f.counters),
		resources: shapeResources(f.resources)
	};
	if (f.actualArrivalTime !== undefined && f.actualArrivalTime !== null && f.actualArrivalTime !== '') {
		// DTO `CheckInFinishRequestDto.actualArrivalTime` is `?string` — a raw
		// number 422s ("should be of type null|string"). Validate numerically,
		// serialize as a string.
		const arrival = Number(f.actualArrivalTime);
		if (Number.isFinite(arrival)) payload.actualArrivalTime = String(arrival);
	}
	return payload;
}

/**
 * Adapt a legacy check-out form state to the `CheckOutInFinishRequestDto`
 * shape. All three time fields are OPTIONAL per the DTO; omit when empty
 * so backend defaults / current-time fallbacks fire correctly.
 *
 * @param {Object} form
 * @param {number|string} [form.flightTime]            Hours (decimal) or seconds.
 * @param {number|string} [form.departureTime]         Epoch seconds.
 * @param {number|string} [form.expectedArrivalTime]   Epoch seconds.
 * @returns {Object} `CheckOutInFinishRequestDto` shape.
 */
export function toCheckOutFinishPayload(form) {
	const f = form || {};
	const payload = {};
	// DTO `CheckOutInFinishRequestDto` declares all three time fields as
	// `?string` — sending raw numbers 422s ("should be of type null|string").
	// Validate numerically, serialize as strings.
	const numericIfPresent = (key, src) => {
		if (src === undefined || src === null || src === '') return;
		const n = Number(src);
		if (Number.isFinite(n)) payload[key] = String(n);
	};
	numericIfPresent('flightTime', f.flightTime);
	numericIfPresent('departureTime', f.departureTime);
	numericIfPresent('expectedArrivalTime', f.expectedArrivalTime);
	return payload;
}

// Sub 10 (2026-05-15): `toPastCheckOutCheckInPayload` REMOVED along with
// the entire combined past-event flow. Past events use the same Check Out
// → Check In sequence as regular events. Symfony route + DTO stay as
// dead contract per user policy.
